/* eslint-disable @typescript-eslint/no-explicit-any */
import { kebabCase } from 'lodash';
import { rgba } from 'polished';
import { PropsWithChildren } from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import { ITimeMachine, Paragraph, tokens } from '@treams/treams-ui';

import { setUserFilterSearchParams } from '../AnalyticsUserFilterSelector';
import { AnalyticsConversationsMetric } from './AnalyticsConversationsMetric';
import { AnalyticsGraphMetric } from './AnalyticsGraphMetric';
import { AnalyticsNumberMetric } from './AnalyticsNumberMetric';

export const AnalyticsMetricContainer = styled(Box)`
	:not(:last-child) {
		border-bottom: 1px solid ${tokens.border.subtle};
	}
`;

const AnalyticsMetricLinkElement = styled(Link)`
	display: block;
	position: relative;
	margin: 4px -9px;
	padding: 0 9px;
	border-radius: 4px;

	:hover {
		${Paragraph} {
			color: ${(props) => props.theme.colors.primary};
		}

		background-color: ${(props) => rgba(props.theme.colors.primary, 0.1)};
	}

	:active {
		background-color: ${(props) => rgba(props.theme.colors.primary, 0.15)};
	}
`;

function AnalyticsMetricLink(
	props: PropsWithChildren<{
		metric: graphql.AnalyticsMetric;
		timeMachine?: ITimeMachine;
		userFilter?: graphql.AnalyticsUserFilter;
	}>,
) {
	const location = useLocation();

	const link = props.metric.id.split('.').map(kebabCase).join('/');

	const query = new URLSearchParams(location.search);

	if (props.timeMachine) {
		query.set('interval', props.timeMachine.interval);
		query.set('cursor', props.timeMachine.urlSafeCurrent[0]);
		query.set('end', props.timeMachine.urlSafeCurrent[1]);
	}

	setUserFilterSearchParams(query, props.userFilter);

	return (
		<AnalyticsMetricLinkElement to={`/analytics/metric/${link}?${query.toString()}`}>
			{props.children}
		</AnalyticsMetricLinkElement>
	);
}

export function valueFromMetric(metric: any): number | undefined {
	const metrics = {
		AnalyticsIntegerMetric: (m: graphql.AnalyticsIntegerMetric) => m.integer,
		AnalyticsFloatMetric: (m: graphql.AnalyticsFloatMetric) => m.float,
		AnalyticsPercentageMetric: (m: graphql.AnalyticsPercentageMetric) => m.percentage,
		AnalyticsFractionMetric: (m: graphql.AnalyticsFractionMetric) => m.value,
		AnalyticsConversationTemplateMetric: (m: graphql.AnalyticsConversationTemplateMetric) =>
			m.value,
	};

	return metrics[metric.__typename]?.(metric);
}

export function AnalyticsMetric(props: {
	metricGroupID: string;
	metric: graphql.AnalyticsMetric;
	totalUsers: number;
	timeMachine: ITimeMachine;
	userFilter?: graphql.AnalyticsUserFilter;
}) {
	switch (props.metric.__typename) {
		case 'AnalyticsIntegerMetric':
		case 'AnalyticsFloatMetric':
		case 'AnalyticsPercentageMetric':
		case 'AnalyticsFractionMetric':
			return (
				<AnalyticsMetricLink
					metric={props.metric as graphql.AnalyticsMetric}
					timeMachine={props.timeMachine}
					userFilter={props.userFilter}
				>
					<AnalyticsNumberMetric
						metricGroupID={props.metricGroupID}
						metric={props.metric as any}
						totalUsers={props.totalUsers}
					/>
				</AnalyticsMetricLink>
			);
		case 'AnalyticsGraphMetric':
			return (
				<AnalyticsGraphMetric
					metricGroupID={props.metricGroupID}
					metric={props.metric as graphql.AnalyticsGraphMetric}
					timeMachine={props.timeMachine}
				/>
			);
		case 'AnalyticsConversationsMetric':
			return (
				<AnalyticsConversationsMetric
					metricGroupID={props.metricGroupID}
					metric={props.metric as graphql.AnalyticsConversationsMetric}
					totalUsers={props.totalUsers}
				/>
			);
	}
	return null;
}
